import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { ArrowLeft, Maximize2, Minimize2, Upload, Link2, Save } from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EQ_BARS = [4, 8, 6, 12, 9, 14, 7, 11, 5, 13, 8, 10, 6];

function StoryPreview({ data, settings, overrideImages }) {
  const {
    hero_photos = [],
    hero_image_url = '',
    hero_bg_image = '',
    song_title = '',
    artist_name = '',
    vinyl_cover_image = '',
    background_music_url = '',
  } = data;

  const mainPhoto = overrideImages.frame || hero_photos?.[0] || hero_image_url || '';
  const bgImage = overrideImages.background || hero_bg_image || mainPhoto;
  const audioUrl = background_music_url || '';
  const audioRef = useRef(null);

  const {
    bgBlur = 8,
    bgOverlay = 50,
    overlayColor = '#000000',
    bgOversize = 30,
    frameSize = 350,
    frameBorder = 3,
    frameBorderColor = 'rgba(255,255,255,0.2)',
    vinylColor = '',
    frameFilter = 'bw',
    heroContentY = 0,
    brandingY = 0,
    titleText = 'The Rhythm of Belonging',
    titleSize = 15,
    titleColor = '#ffffff',
    subtitleText = 'Every celebration has a rhythm. We design how it flows.',
    subtitleSize = 13,
    subtitleColor = 'rgba(255,255,255,0.7)',
    line3Text = 'A seamless seating experience, thoughtfully designed',
    line3Size = 11,
    line3Color = 'rgba(255,255,255,0.45)',
  } = settings;

  const frameFilterStyle = frameFilter === 'bw'
    ? 'grayscale(100%) contrast(1.1)'
    : 'none';

  const rgba = (hex, alpha) => {
    const h = (hex || '#000000').replace('#', '');
    const r = parseInt(h.substring(0, 2), 16) || 0;
    const g = parseInt(h.substring(2, 4), 16) || 0;
    const b = parseInt(h.substring(4, 6), 16) || 0;
    return `rgba(${r},${g},${b},${alpha})`;
  };

  // Animation state — tap to toggle
  const [isAnimating, setIsAnimating] = useState(false);
  const hasStarted = useRef(false);

  useEffect(() => {
    const startAnimation = () => {
      if (hasStarted.current) return;
      hasStarted.current = true;
      setIsAnimating(true);
      // Start audio on first interaction
      const audio = audioRef.current;
      if (audio) {
        audio.volume = 0;
        audio.play().then(() => {
          let vol = 0;
          const fade = setInterval(() => { vol = Math.min(1, vol + 0.1); audio.volume = vol; if (vol >= 1) clearInterval(fade); }, 80);
        }).catch(() => {});
      }
    };
    document.addEventListener('click', startAnimation, { once: true });
    document.addEventListener('touchstart', startAnimation, { once: true });
    document.addEventListener('scroll', startAnimation, { once: true });
    return () => {
      document.removeEventListener('click', startAnimation);
      document.removeEventListener('touchstart', startAnimation);
      document.removeEventListener('scroll', startAnimation);
    };
  }, []);

  const toggleAnimation = useCallback(() => {
    if (!hasStarted.current) {
      hasStarted.current = true;
    }
    setIsAnimating(prev => {
      const next = !prev;
      const audio = audioRef.current;
      if (audio) {
        if (next) {
          audio.volume = 0;
          audio.play().then(() => {
            // Fade in
            let vol = 0;
            const fade = setInterval(() => { vol = Math.min(1, vol + 0.1); audio.volume = vol; if (vol >= 1) clearInterval(fade); }, 80);
          }).catch(() => {});
        } else {
          audio.pause();
        }
      }
      return next;
    });
  }, []);

  return (
    <div
      className="relative min-h-screen font-serif"
      style={{ backgroundColor: '#000', color: '#fff' }}
      data-testid="story-preview"
    >
      {/* Audio element for background music */}
      {audioUrl && <audio ref={audioRef} src={audioUrl} loop preload="auto" />}

      <style>{`
        @keyframes eqBounce {
          0% { transform: scaleY(0.25); }
          25% { transform: scaleY(0.9); }
          50% { transform: scaleY(0.4); }
          75% { transform: scaleY(1); }
          100% { transform: scaleY(0.25); }
        }
      `}</style>

      {/* Background image */}
      {bgImage && (
        <div className="absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
          <img
            src={bgImage} alt="" draggable={false}
            className="absolute"
            style={{
              top: `-${Math.ceil(bgOversize / 2)}px`,
              left: `-${Math.ceil(bgOversize / 2)}px`,
              width: `calc(100% + ${bgOversize}px)`,
              height: `calc(100% + ${bgOversize}px)`,
              objectFit: 'cover',
              filter: bgBlur > 0 ? `blur(${bgBlur}px)` : 'none',
            }}
          />
          {bgOverlay > 0 && (
            <div className="absolute inset-0" style={{ backgroundColor: rgba(overlayColor, bgOverlay / 100) }} />
          )}
        </div>
      )}

      {/* Content */}
      <div
        className="relative z-10 flex flex-col items-center px-6 pt-6 pb-8"
        style={{ minHeight: '100vh' }}
      >

        {/* Frame + Vinyl + Title — movable together */}
        <div className="flex-1 flex flex-col items-center justify-center w-full" style={{ transform: `translateY(${heroContentY}px)` }}>
          <div className="relative" style={{ width: `${frameSize}px`, margin: '0 auto' }}>
          <div
            className="relative overflow-hidden"
            style={{ aspectRatio: '1/1', border: `${frameBorder}px solid ${frameBorderColor}` }}
          >
            {mainPhoto ? (
              <img
                src={mainPhoto} alt="" className="w-full h-full object-cover"
                style={{ filter: frameFilterStyle }} draggable={false}
              />
            ) : (
              <div className="w-full h-full bg-neutral-900 flex items-center justify-center">
                <p className="text-sm text-white/20">Upload a photo</p>
              </div>
            )}

            {/* Equalizer — animate on tap/scroll */}
            <div className="absolute bottom-3 left-3 flex items-end gap-[2px] z-20">
              {EQ_BARS.map((h, i) => (
                <div
                  key={i} className="rounded-sm origin-bottom"
                  style={{
                    width: '2.5px', height: `${h}px`,
                    backgroundColor: 'rgba(255,255,255,0.6)',
                    ...(isAnimating ? {
                      animation: `eqBounce ${0.6 + (i % 5) * 0.2}s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
                      animationDelay: `${i * 0.08}s`,
                    } : {
                      transform: 'scaleY(0.3)',
                      transition: 'transform 0.4s ease',
                    }),
                  }}
                />
              ))}
            </div>

            {/* Song info — bottom with gradient like Noir */}
            {(song_title || artist_name) && (
              <div className="absolute bottom-0 left-0 right-0 z-20 px-4 pb-4 pt-12 bg-gradient-to-t from-black/80 via-black/40 to-transparent text-center">
                <p
                  className="text-lg font-light tracking-wide"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#fff' }}
                >
                  {song_title || 'Our Song'}
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {artist_name || 'Artist'}
                </p>
              </div>
            )}
          </div>

          {/* Vinyl Record — 130px like Noir */}
          <div
            className="absolute z-20 cursor-pointer"
            style={{ top: '5%', right: '-15px', width: '130px' }}
            onClick={toggleAnimation}
            data-testid="story-vinyl-disc"
          >
            <div className="relative" style={{ paddingBottom: '100%' }}>
              <motion.div
                animate={{ rotate: isAnimating ? 360 : 0 }}
                transition={{ duration: 4, ease: 'linear', repeat: isAnimating ? Infinity : 0 }}
                className="absolute inset-0 rounded-full"
                style={{
                  background: vinylColor
                    ? `radial-gradient(circle at center, ${vinylColor} 0%, ${vinylColor}cc 50%, ${vinylColor}99 70%, ${vinylColor}66 100%)`
                    : 'radial-gradient(circle at center, #2f2f2f 0%, #171717 50%, #0d0d0d 70%, #000 100%)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
                  border: '2px solid rgba(255,255,255,0.15)',
                }}
              >
                {[10, 18, 26, 34].map((inset) => (
                  <div key={inset} className="absolute rounded-full border border-white/[0.08]" style={{ inset: `${inset}%` }} />
                ))}
                <div
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full overflow-hidden border border-white/20"
                  style={{ width: '52%', height: '52%' }}
                >
                  {(vinyl_cover_image || mainPhoto) ? (
                    <img src={vinyl_cover_image || mainPhoto} alt="" className="w-full h-full object-cover" style={{ filter: 'grayscale(100%)' }} draggable={false} />
                  ) : (
                    <div className="w-full h-full bg-neutral-700" />
                  )}
                </div>
                <div className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black border border-white/30 z-10" />
                {/* Play hint — only when stopped */}
                {!isAnimating && (
                  <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/20">
                    <span className="ml-0.5 block h-0 w-0" style={{ borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '12px solid rgba(255,255,255,0.7)' }} />
                  </div>
                )}
              </motion.div>

              {/* Tonearm */}
              <motion.div
                animate={{ rotate: isAnimating ? 12 : 0 }}
                transition={{ duration: 0.6, ease: 'easeInOut' }}
                className="absolute z-30"
                style={{ right: '-12%', top: '-10%', width: '40%', height: '65%', transformOrigin: '75% 8%' }}
              >
                <img src="/assets/tonearm.png" alt="" className="w-full h-full" style={{ objectFit: 'contain' }} draggable={false} />
              </motion.div>
            </div>
          </div>
        </div>

        {/* Title + Subtitle + Tagline below frame */}
        <div className="text-center mt-9">
          {titleText && (
            <h1
              className="font-light tracking-[0.2em] uppercase leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: titleColor, fontSize: `${titleSize}px` }}
            >
              {titleText}
            </h1>
          )}
          {subtitleText && (
            <p
              className="mt-2 font-light tracking-wide italic"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: subtitleColor, fontSize: `${subtitleSize}px` }}
            >
              {subtitleText}
            </p>
          )}
          {line3Text && (
            <p
              className="mt-1.5 font-light tracking-wide"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: line3Color, fontSize: `${line3Size}px` }}
            >
              {line3Text}
            </p>
          )}
        </div>
        </div>

        {/* Bottom branding — independently movable */}
        <div className="text-center pt-8 pb-2" style={{ transform: `translateY(${brandingY}px)` }}>
          <div className="flex items-center justify-center gap-3">
            <div className="h-px w-8" style={{ backgroundColor: 'rgba(212,175,55,0.3)' }} />
            <p className="text-[11px] tracking-[0.3em] uppercase" style={{ color: 'rgba(212,175,55,0.6)' }}>
              sitoa.studio
            </p>
            <div className="h-px w-8" style={{ backgroundColor: 'rgba(212,175,55,0.3)' }} />
          </div>
        </div>
      </div>
    </div>
  );
}


export default function InstagramStory() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const previewRef = useRef(null);
  const [rsvpData, setRsvpData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const accessToken = params.get('access');
  const isPublicAccess = !!accessToken;
  const isAdmin = !isPublicAccess && !!user;

  const [overrideImages, setOverrideImages] = useState({ background: '', frame: '' });

  const [settings, setSettings] = useState({
    bgBlur: 8,
    bgOverlay: 50,
    overlayColor: '#000000',
    bgOversize: 30,
    frameSize: 350,
    frameBorder: 3,
    frameBorderColor: 'rgba(255,255,255,0.2)',
    vinylColor: '',
    frameFilter: 'bw',
    heroContentY: 0,
    brandingY: 0,
    titleText: 'The Rhythm of Belonging',
    titleSize: 15,
    titleColor: '#ffffff',
    subtitleText: 'Every celebration has a rhythm. We design how it flows.',
    subtitleSize: 13,
    subtitleColor: 'rgba(255,255,255,0.7)',
    line3Text: 'A seamless seating experience, thoughtfully designed',
    line3Size: 11,
    line3Color: 'rgba(255,255,255,0.45)',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        let res;
        if (accessToken) {
          res = await axios.get(`${API}/story-access/${accessToken}`);
        } else {
          const token = localStorage.getItem('token');
          if (!token) { setLoading(false); return; }
          res = await axios.get(`${API}/events/${eventId}/rsvp-page`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        if (res.data) {
          setRsvpData(res.data);
          const saved = res.data.story_settings || {};
          setSettings(prev => ({
            ...prev,
            bgBlur: saved.bgBlur ?? res.data.bg_blur ?? 8,
            bgOverlay: saved.bgOverlay ?? 50,
            overlayColor: saved.overlayColor ?? '#000000',
            bgOversize: saved.bgOversize ?? res.data.bg_oversize ?? 30,
            frameSize: saved.frameSize ?? 350,
            frameBorder: saved.frameBorder ?? res.data.frame_border ?? 3,
            frameBorderColor: saved.frameBorderColor ?? res.data.frame_border_color ?? 'rgba(255,255,255,0.2)',
            vinylColor: saved.vinylColor ?? res.data.vinyl_color ?? '',
            frameFilter: saved.frameFilter ?? 'bw',
            heroContentY: saved.heroContentY ?? 0,
            brandingY: saved.brandingY ?? 0,
          }));
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 410) toast.error('Share link expired. Generate a new one.');
        else if (status === 404) toast.error('Invalid link or no RSVP page found');
        else toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [eventId, accessToken]);

  const updateSetting = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  }, []);

  const saveSettings = useCallback(async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/events/${eventId}/rsvp-page`, {
        story_settings: settings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasChanges(false);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }, [eventId, settings]);

  const handleImageUpload = useCallback(async (type, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API}/events/${eventId}/rsvp-page/upload-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data?.image_url) {
        setOverrideImages(prev => ({ ...prev, [type]: res.data.image_url }));
        toast.success(`${type === 'background' ? 'Background' : 'Frame'} image uploaded`);
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  }, [eventId]);

  const copyStoryLink = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/events/${eventId}/story-share-token`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const shareToken = res.data.token;
      const url = `${window.location.origin}/events/${eventId}/instagram-story?access=${shareToken}`;
      navigator.clipboard.writeText(url);
      toast.success('Link copied! Opens without login for 1 hour');
    } catch {
      const url = `${window.location.origin}/events/${eventId}/instagram-story`;
      navigator.clipboard.writeText(url);
      toast.success('Link copied (login required)');
    }
  }, [eventId]);

  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      const el = previewRef.current;
      if (el?.requestFullscreen) el.requestFullscreen();
      else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      setIsFullscreen(false);
    }
  }, [isFullscreen]);

  useEffect(() => {
    const handler = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setIsFullscreen(false);
      }
    };
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-pulse text-[#D4AF37] font-heading text-xl">Loading...</div>
      </div>
    );
  }

  if (!rsvpData) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white gap-4">
        <p>{isPublicAccess ? 'This link has expired or is invalid.' : 'No RSVP page found.'}</p>
        {!isPublicAccess && (
          <Button onClick={() => navigate(`/events/${eventId}`)} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Event
          </Button>
        )}
      </div>
    );
  }

  // PUBLIC ACCESS: Just the story content, full page
  if (isPublicAccess) {
    return <StoryPreview data={rsvpData} settings={settings} overrideImages={overrideImages} />;
  }

  // ADMIN VIEW: Preview + Controls
  const SliderControl = ({ label, value, min, max, step = 1, onChange, testId, unit }) => (
    <div>
      <div className="flex justify-between text-xs text-gray-400 mb-1">
        <span>{label}</span>
        <span>{value}{unit || 'px'}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full accent-amber-500" data-testid={testId}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[#020617]/95 backdrop-blur border-b border-white/10 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/events/${eventId}`)} className="text-gray-400 hover:text-white" data-testid="back-to-event">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-white font-medium">Instagram Story</h1>
              <p className="text-xs text-gray-500">Copy link to open on phone. Tap to start animation.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={saveSettings} disabled={saving || !hasChanges}
              className={`border-gray-700 hover:bg-gray-800 ${hasChanges ? 'text-amber-400 border-amber-500/40' : 'text-gray-500'}`} data-testid="save-story-settings">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outline" size="sm" onClick={copyStoryLink} className="text-gray-300 border-gray-700 hover:bg-gray-800" data-testid="copy-story-link">
              <Link2 className="w-4 h-4 mr-2" /> Copy Link
            </Button>
            <Button variant="outline" size="sm" onClick={toggleFullscreen} className="text-gray-300 border-gray-700 hover:bg-gray-800" data-testid="fullscreen-toggle">
              {isFullscreen ? <Minimize2 className="w-4 h-4 mr-2" /> : <Maximize2 className="w-4 h-4 mr-2" />}
              {isFullscreen ? 'Exit' : 'Fullscreen'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Preview */}
          <div className="lg:col-span-2 flex justify-center">
            <div
              ref={previewRef}
              className="overflow-hidden"
              style={isFullscreen ? {
                width: '100vw', height: '100vh',
              } : {
                width: '100%', maxWidth: '420px', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '8px',
              }}
            >
              <StoryPreview data={rsvpData} settings={settings} overrideImages={overrideImages} />
            </div>
          </div>

          {/* Controls */}
          {!isFullscreen && (
            <div className="space-y-4">
              <div className="bg-gray-900/50 rounded-xl border border-white/10 p-5 space-y-4">
                <h3 className="text-sm font-medium text-white tracking-wide uppercase">Images</h3>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col items-center gap-2 p-3 rounded-lg border border-dashed border-gray-600 hover:border-amber-500/50 cursor-pointer transition-colors">
                    <Upload className="w-4 h-4 text-gray-400" />
                    <span className="text-[10px] text-gray-400 text-center">Background</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload('background', e.target.files[0])} disabled={uploading} data-testid="upload-story-bg" />
                    {overrideImages.background && <div className="w-full h-1 bg-amber-500/50 rounded mt-1" />}
                  </label>
                  <label className="flex flex-col items-center gap-2 p-3 rounded-lg border border-dashed border-gray-600 hover:border-amber-500/50 cursor-pointer transition-colors">
                    <Upload className="w-4 h-4 text-gray-400" />
                    <span className="text-[10px] text-gray-400 text-center">Frame Photo</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload('frame', e.target.files[0])} disabled={uploading} data-testid="upload-story-frame" />
                    {overrideImages.frame && <div className="w-full h-1 bg-amber-500/50 rounded mt-1" />}
                  </label>
                </div>
                {uploading && <p className="text-xs text-amber-400 text-center">Uploading...</p>}
              </div>

              <div className="bg-gray-900/50 rounded-xl border border-white/10 p-5 space-y-5">
                <h3 className="text-sm font-medium text-white tracking-wide uppercase">Customize</h3>

                <div>
                  <Label className="text-xs text-gray-400 block mb-2">Frame Image</Label>
                  <div className="flex gap-2">
                    {[{ id: 'bw', label: 'Black & White' }, { id: 'original', label: 'Original' }].map(opt => (
                      <button
                        key={opt.id} onClick={() => updateSetting('frameFilter', opt.id)}
                        className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
                          settings.frameFilter === opt.id
                            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                            : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
                        }`}
                        data-testid={`frame-filter-${opt.id}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-xs text-gray-400 block">Background</Label>
                  <SliderControl label="Blur" value={settings.bgBlur} min={0} max={30} onChange={v => updateSetting('bgBlur', v)} testId="story-blur" />
                  <SliderControl label="Dark Overlay" value={settings.bgOverlay} min={0} max={90} onChange={v => updateSetting('bgOverlay', v)} testId="story-overlay" unit="%" />
                  <SliderControl label="Oversize" value={settings.bgOversize} min={0} max={50} onChange={v => updateSetting('bgOversize', v)} testId="story-oversize" />
                </div>

                <div>
                  <Label className="text-xs text-gray-400 block mb-2">Overlay Color</Label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={settings.overlayColor} onChange={(e) => updateSetting('overlayColor', e.target.value)}
                      className="w-10 h-10 rounded-lg cursor-pointer border border-gray-700 bg-transparent" data-testid="story-overlay-color" />
                    <span className="text-xs text-gray-500 font-mono">{settings.overlayColor}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-xs text-gray-400 block">Frame</Label>
                  <SliderControl label="Size" value={settings.frameSize} min={200} max={400} step={10} onChange={v => updateSetting('frameSize', v)} testId="story-frame-size" />
                  <SliderControl label="Border" value={settings.frameBorder} min={0} max={24} onChange={v => updateSetting('frameBorder', v)} testId="story-frame-border" />
                  <div>
                    <div className="flex justify-between text-xs text-gray-400 mb-1"><span>Border Color</span></div>
                    <input type="color" value={settings.frameBorderColor?.startsWith('#') ? settings.frameBorderColor : '#ffffff'}
                      onChange={(e) => updateSetting('frameBorderColor', e.target.value)}
                      className="w-full h-8 rounded cursor-pointer border border-gray-700" data-testid="story-frame-border-color" />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-gray-400 block mb-2">Vinyl Record Color</Label>
                  <input type="color" value={settings.vinylColor || '#1a1a1a'} onChange={(e) => updateSetting('vinylColor', e.target.value)}
                    className="w-full h-8 rounded cursor-pointer border border-gray-700" data-testid="story-vinyl-color" />
                </div>

                <SliderControl label="Content Position" value={settings.heroContentY} min={-150} max={150} step={5} onChange={v => updateSetting('heroContentY', v)} testId="story-content-y" />
                <SliderControl label="Branding Position" value={settings.brandingY} min={-150} max={150} step={5} onChange={v => updateSetting('brandingY', v)} testId="story-branding-y" />
              </div>

              {/* Text Customization */}
              <div className="bg-gray-900/50 rounded-xl border border-white/10 p-5 space-y-5">
                <h3 className="text-sm font-medium text-white tracking-wide uppercase">Text</h3>

                {[
                  { key: 'title', label: 'Title', textKey: 'titleText', sizeKey: 'titleSize', colorKey: 'titleColor' },
                  { key: 'subtitle', label: 'Subtitle', textKey: 'subtitleText', sizeKey: 'subtitleSize', colorKey: 'subtitleColor' },
                  { key: 'line3', label: 'Line 3', textKey: 'line3Text', sizeKey: 'line3Size', colorKey: 'line3Color' },
                ].map(({ key, label, textKey, sizeKey, colorKey }) => (
                  <div key={key} className="space-y-2">
                    <Label className="text-xs text-gray-400 block">{label}</Label>
                    <input
                      type="text"
                      value={settings[textKey]}
                      onChange={(e) => updateSetting(textKey, e.target.value)}
                      placeholder={label}
                      className="w-full bg-gray-800 text-white text-xs rounded-lg px-3 py-2 border border-gray-700 focus:border-amber-500/50 outline-none"
                      data-testid={`story-${key}-text`}
                    />
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <SliderControl label="Size" value={settings[sizeKey]} min={8} max={28} onChange={v => updateSetting(sizeKey, v)} testId={`story-${key}-size`} />
                      </div>
                      <input
                        type="color"
                        value={settings[colorKey]?.startsWith('#') || settings[colorKey]?.startsWith('rgb') ? (settings[colorKey].startsWith('#') ? settings[colorKey] : '#ffffff') : '#ffffff'}
                        onChange={(e) => updateSetting(colorKey, e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border border-gray-700 bg-transparent shrink-0"
                        data-testid={`story-${key}-color`}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gray-900/30 rounded-xl border border-white/5 p-4">
                <p className="text-xs text-gray-500 leading-relaxed">
                  <span className="text-amber-400 font-medium">How to use:</span> Use
                  <span className="text-gray-300"> Copy Link</span> to open on your phone. Tap to start animation, screen record, and upload to Instagram with music from their library.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
